const fileStorageFileName = 'crmAppState.json';

const checkFileLengthAfterSaving = false;

const incrementSize = 5 * 1024 * 1024; // increment in 5MB chunks
let currentFileSystemSize = 10 * 1024 * 2014; // 10 megabytes to start

let saveStartTime = 0; // milliseconds so far in epoch
let endSerializationTime = 0;
let endHashTime = 0;
let endRequestQuotaTime = 0;
let endRequestFileSystemTime = 0;
let endGetFileTime = 0;
let endCreateWriterTime = 0;
let endSeekToZeroTime = 0;
let startWriteTime = 0;
let endWriteTime = 0;
let endTruncateTime = 0;

let fileSystem = null;
let fileEntry = null;
let singleFileWriter = null;

let busySaving = false;
let hashOfCurrentSave = '';
let jsonPendingSave = null;
let hashOfPendingSave = '';
let hashOfLatestSave = '';

export const saveStateToSandboxedFileSystem = (state) => {
  saveStartTime = Date.now();
  const serializedState = JSON.stringify(state);
  endSerializationTime = Date.now();
  const hash = computeHash(serializedState);
  endHashTime = Date.now();
  const jsonLength = serializedState.length;
  log(`\nreceived Rq to save JSON string of length: ${jsonLength} with hash: '${hash}'`);

  if (busySaving) {
    log(`  We're currently busy saving JSON with hash '${hashOfCurrentSave}'`);
    if (hash === hashOfPendingSave) {
      // we are already set to save this guy, so no more work to be done here
      log('  This guy is already set to be the next save, so skipping');
      return;
    } else if (hash === hashOfCurrentSave && !hashOfPendingSave) {
      // we are currently saving this guy and not to overwrite that, so no work to be done here
      log('  We are currently saving this state and not expecting to overwrite it, so skipping');
      return;
    } else {
      log('  Setting new values of jsonPendingSave and hashOfPendingSave to save this guy next');
      jsonPendingSave = serializedState;
      hashOfPendingSave = hash;
    }
    reportSerializationAndHashTiming();
  } else {
    // not busySaving
    log("  We're currently NOT busy saving");
    if (hash === hashOfLatestSave) {
      // we just saved this guy, so no need to repeat that
      log('  Just saved this same state, so skipping');
      reportSerializationAndHashTiming();
      return;
    } else {
      log('  Setting new values of jsonPendingSave and hashOfPendingSave to save this guy right now');
      jsonPendingSave = serializedState;
      hashOfPendingSave = hash;
      savePendingJsonIfAny();
    }
  }
};

const savePendingJsonIfAny = () => {
  if (!jsonPendingSave) {
    // No "remaining" state to save, so just return..
    log("  At top of savePendingJsonIfAny(), but there's nothing more to save");
    busySaving = false;
    return;
  }
  log(`  At top of savePendingJsonIfAny() and have json to save with hash '${hashOfPendingSave}'`);
  busySaving = true;
  hashOfCurrentSave = hashOfPendingSave;
  hashOfPendingSave = null;
  const tempJsonToSave = jsonPendingSave;
  jsonPendingSave = null;
  const successfullWriteContinuation = () => {
    hashOfLatestSave = hashOfCurrentSave;
    hashOfCurrentSave = '';
    log(`in successfullWriteContinuation()..\n  calling savePendingJsonIfAny() after saving ${hashOfLatestSave}`);
    savePendingJsonIfAny();
  };
  writeToFile(tempJsonToSave, successfullWriteContinuation);
};

const writeToFile = (jsonToSave, successfullWriteContinuation) => {
  const jsonLength = jsonToSave.length;
  log(`  At top of writeToFile() for string of length: ${jsonLength}; hash: ${hashOfCurrentSave}`);

  // set fake (but probably more accurate) start times
  const serializeDelta = endSerializationTime - saveStartTime;
  const hashDelta = endHashTime - saveStartTime;
  const now = Date.now();
  saveStartTime = now - hashDelta;
  endSerializationTime = saveStartTime + serializeDelta;
  endHashTime = saveStartTime + hashDelta;

  try {
    const nBytesNeeded = jsonLength * 2; // assuming 2 bytes per character
    if (!fileSystem || currentFileSystemSize < nBytesNeeded) {
      if (!fileSystem) {
        log('Taking slow path because !fileSystem');
      } else {
        log(
          `Taking slow path because currentFileSystemSize (${currentFileSystemSize}) < nBytesNeeded (${nBytesNeeded})`
        );
      }
      while (currentFileSystemSize < nBytesNeeded) {
        currentFileSystemSize += incrementSize;
        log(`currentFileSystemSize set to ${currentFileSystemSize} bytes`);
      }

      // Use the Quota Management API: https://developer.chrome.com/docs/apps/offline_storage/
      navigator.webkitPersistentStorage.requestQuota(
        currentFileSystemSize,
        (grantedBytes) => handleRequestQuotaSuccess(grantedBytes, jsonToSave, successfullWriteContinuation),
        (rqQuotaError) => handleRequestQuotaError(rqQuotaError)
      );
    } else {
      // we already have a big enough file system
      // Record these operations as very fast, since we're skipping them.
      endRequestQuotaTime = Date.now();
      endRequestFileSystemTime = endRequestQuotaTime;
      endGetFileTime = endRequestFileSystemTime;
      log(`we have fileSystem and big enough quota, so taking fast path`);

      // This is (poorly) documented at https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileEntry/createWriter
      if (singleFileWriter) {
        handleWriteWithFileWriter(singleFileWriter, jsonToSave, successfullWriteContinuation);
      } else {
        fileEntry.createWriter(
          (fileWriter) => {
            singleFileWriter = fileWriter;
            handleWriteWithFileWriter(fileWriter, jsonToSave, successfullWriteContinuation);
          },
          // this is the failure handler for fileEntry.createWriter()
          (fileError) => {
            log(`Error in fileEntry.createWriter(). Error Code: ${fileError.code}`);
            // Error codes can be found here:
            // https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file/index.html#list-of-error-codes-and-meanings
          }
        );
      }
    }
  } catch (err) {
    log(`Error in writeToFile(): ${err.message}`);
  }
};

const handleWriteWithFileWriter = (fileWriter, jsonToSave, successfullWriteContinuation) => {
  endCreateWriterTime = Date.now();
  log('Successfully executed fileEntry.createWriter()');

  // start by truncating the file so our write starts at the beginning of the file
  // fileWriter.write({ type: 'seek', position: 0 });
  fileWriter.seek(0);
  endSeekToZeroTime = Date.now();

  // arrange to truncate after finished writing
  // Decent example of "async'y" approach to truncate at https://stackify.dev/239287-using-the-html5-filewriter-truncate-method

  let truncated = false;
  fileWriter.onwriteend = (e) => {
    if (!truncated) {
      endWriteTime = Date.now();
      const position = fileWriter.position;
      // log(`hoping to truncate to length: ${position}`);
      fileWriter.truncate(position);
      endTruncateTime = Date.now();
      truncated = true; // avoid repetition
      return;
    }
    // log('Write completed.');

    reportTiming();

    if (checkFileLengthAfterSaving) {
      // let's check the length of the file..
      const fsRoot = fileSystem.root;
      fsRoot.getFile(
        fileStorageFileName,
        // look at the table in https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryEntry/getFile
        // I'm not sure a new one is actually created. Looks to me like we may be appending
        { create: false }, // insist it be present
        (fileEntry) => {
          fileEntry.file(
            (file) => {
              const fileSize = file.size;
              const blobSize = jsonToSave.length;
              console.log(`after saving blob of size ${blobSize}b file is of size ${fileSize}b`);
              successfullWriteContinuation();
            },
            (domException) => {
              log(`domException: ${domException}`);
            }
          );
        },
        (domException) => handleGetFileError(domException)
      );
    } else {
      successfullWriteContinuation();
    }
  };

  const blob = new Blob([jsonToSave], { type: 'text/plain' });
  startWriteTime = Date.now();
  fileWriter.write(blob);
};

const handleRequestQuotaSuccess = (grantedBytes, jsonToSave, successfullWriteContinuation) => {
  endRequestQuotaTime = Date.now();
  log(`requestQuota yielded grantedBytes: ${grantedBytes}`);
  window.webkitRequestFileSystem(
    navigator.webkitPersistentStorage,
    grantedBytes,
    (fileSystem) => handleRequestFileSystemSuccess(fileSystem, jsonToSave, successfullWriteContinuation),
    (fileError) => handleRequestFileSystemError(fileError)
  );
};

const handleRequestFileSystemSuccess = (fileSys, jsonToSave, successfullWriteContinuation) => {
  endRequestFileSystemTime = Date.now();

  log('Successfully requested file system');

  // store for later use without call to webkitRequestFileSystem().
  fileSystem = fileSys;

  // get the root directory
  const rootDirectoryEntry = fileSystem.root;

  // get the file where we want to store our serialized state, and store it
  rootDirectoryEntry.getFile(
    fileStorageFileName,
    // look at the table in https://developer.mozilla.org/en-US/docs/Web/API/FileSystemDirectoryEntry/getFile
    // I'm not sure a new one is actually created. Looks to me like we may be appending
    { create: true, exclusive: false }, // create if not present, o/w create a new one
    (fileEntry) => handleGetFileSuccess(fileEntry, jsonToSave, successfullWriteContinuation),
    (domException) => handleGetFileError(domException)
  );
};

const handleGetFileSuccess = (fileEntry, jsonToSave, successfullWriteContinuation) => {
  endGetFileTime = Date.now();

  log('Successfully got file (entry)');

  // This is (poorly) documented at https://developer.mozilla.org/en-US/docs/Web/API/FileSystemFileEntry/createWriter
  // const file = fileEntry.file();

  fileEntry.createWriter(
    (fileWriter) => {
      endCreateWriterTime = Date.now();
      log('Successfully executed fileEntry.createWriter()');
      const blob = new Blob([jsonToSave], { type: 'text/plain' });
      fileWriter.write(blob);
      endWriteTime = Date.now();
      reportTiming();
      successfullWriteContinuation();
    },
    (fileError) => log(`Error in fileEntry.createWriter(). Error Code: ${fileError.code}`)
    // Error codes can be found here:
    // https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file/index.html#list-of-error-codes-and-meanings
  );
};

const reportTiming = () => {
  const totalTime = Date.now() - saveStartTime;
  log(`total Save Time: ${totalTime}`);

  const msToSerialize = endSerializationTime - saveStartTime;
  log(`  msToSerialize: ${msToSerialize}`);

  const msToRequestQuota = endRequestQuotaTime - endSerializationTime;
  log(`  msToRequestQuota: ${msToRequestQuota}`);

  const msToRequestFile = endRequestFileSystemTime - endRequestQuotaTime;
  log(`  msToRequestFileSystem: ${msToRequestFile}`);

  const msToGetFile = endGetFileTime - endRequestFileSystemTime;
  log(`  msToGetFile: ${msToGetFile}`);

  const msToCreateWriter = endCreateWriterTime - endGetFileTime;
  log(`  msToCreateWriter: ${msToCreateWriter}`);

  const msToSeekToZero = endSeekToZeroTime - endCreateWriterTime;
  log(`  msToSeekToZero: ${msToSeekToZero}`);

  const msToWrite = endWriteTime - startWriteTime;
  log(`  msToWrite: ${msToWrite}`);

  const msToTruncate = endTruncateTime - endWriteTime;
  log(`  msToTruncate: ${msToTruncate}`);
};

const reportSerializationAndHashTiming = () => {
  const totalTime = Date.now() - saveStartTime;
  log(`total Serialization and Hash Time: ${totalTime}`);

  const msToSerialize = endSerializationTime - saveStartTime;
  log(`  msToSerialize: ${msToSerialize}`);

  const msToHash = endHashTime - endSerializationTime;
  log(`  msToHash: ${msToHash}`);
};

const handleGetFileError = (domException) => {
  log(`Error in getFile(): ${domException.message}`);
};

const handleRequestFileSystemError = (fileError) => {
  // Error codes can be found here:
  // https://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file/index.html#list-of-error-codes-and-meanings
  log(`Error in window.webkitRequestFileSystem(): ${fileError.code}`);
};

const handleRequestQuotaError = (error) => {
  log('Error in requestQuota()', error);
};

export const loadStateFromSandboxedFileSystem = (continuationFunction, switchToLocalStorageFunction) => {
  navigator.webkitPersistentStorage.requestQuota(
    currentFileSystemSize,
    (grantedBytes) =>
      handleRequestQuotaSuccessForLoading(grantedBytes, continuationFunction, switchToLocalStorageFunction),
    (rqQuotaError) => handleRequestQuotaError(rqQuotaError)
  );
};

const handleRequestQuotaSuccessForLoading = (grantedBytes, continuationFunction, switchToLocalStorageFunction) => {
  if (grantedBytes === 0) {
    // This is where we get if the User declines to let us use file storage
    switchToLocalStorageFunction();
    continuationFunction(undefined);
    return;
  }
  window.webkitRequestFileSystem(
    navigator.webkitPersistentStorage,
    grantedBytes,
    (fileSystem) => handleRequestFileSystemSuccessForLoading(fileSystem, continuationFunction),
    (fileError) => handleRequestFileSystemError(fileError)
  );
};

const handleRequestFileSystemSuccessForLoading = (fileSystem, continuationFunction) => {
  // get the root directory
  const rootDirectoryEntry = fileSystem.root;

  // get the file where we want to store our serialized state, and store it
  rootDirectoryEntry.getFile(
    fileStorageFileName,
    { create: true, exclusive: false }, // create if not present, but allow to be present
    (fileEntry) => handleGetFileSuccessForLoading(fileEntry, continuationFunction),
    (domException) => handleGetFileError(domException)
  );
};

const handleGetFileSuccessForLoading = (fEntry, continuationFunction) => {
  fileEntry = fEntry;
  fEntry.file(
    (file) => handleFileSuccess(file, fEntry, continuationFunction),
    (domException) => handleFileError(domException)
  );
};

const handleFileSuccess = (file, fileEntry, continuationFunction) => {
  log(`reading file '${file.name}' of size: ${file.size} bytes`);
  if (file.size === 0) {
    continuationFunction(undefined);
    return;
  }
  // found this at https://developer.mozilla.org/en-US/docs/Web/API/FileReader/FileReader
  const reader = new FileReader();
  reader.onload = (evt) => {
    const fileText = evt.target.result;
    const serializedState = fileText; // code as documentation -- :)
    try {
      const parsedState = JSON.parse(serializedState);
      continuationFunction(parsedState);
    } catch (err) {
      log(`JSON parsing error: ${err.message}, parsing string of length ${serializedState.length}`);
      fileEntry.remove(
        () => log(`Successfully removed malformed file '${file.name}'`),
        () => log(`Failed to remove malformed file '${file.name}'`)
      );
      continuationFunction(undefined);
    }
  };
  reader.readAsText(file);
};

const handleFileError = (domException) => {
  log(`Error in file(): ${domException.message}`);
};

const computeHash = (str) => {
  // found at https://gist.github.com/jlevy/c246006675becc446360a798e2b2d781
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash; // Convert to 32bit integer
  }
  return new Uint32Array([hash])[0].toString(36);
};

const skipLogging = true;
const log = (str) => {
  if (!skipLogging) {
    console.log(str);
  }
};
